import { ArrowRight, Eraser, Loader2, Mic, MicOff, Pencil, Video } from "lucide-react";

const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Break long must-have phrases ("Java microservices with Quarkus or Spring Boot") into matchable terms.
export function skillTerms(skills = []) {
  const out = new Set();
  skills.forEach((s) => String(s).split(/,|\/|\(|\)|;|\bwith\b|\bor\b|\band\b|\bexperience\b|\bproduction\b|\bhands-on\b/i)
    .map((t) => t.trim()).filter((t) => t.length >= 2 && t.length <= 40).forEach((t) => out.add(t)));
  return [...out].sort((a, b) => b.length - a.length);
}

function Highlighted({ text, terms }) {
  if (!terms.length || !text) return text;
  const pat = terms.map((t) => `${/^\w/.test(t) ? "\\b" : ""}${esc(t)}${/\w$/.test(t) ? "\\b" : ""}`).join("|");
  const re = new RegExp(`(${pat})`, "gi");
  return text.split(re).map((part, i) => (i % 2 === 1
    ? <mark key={i} data-testid="skill-highlight" className="bg-violet-500/25 text-violet-100 rounded px-0.5">{part}</mark>
    : part));
}

const TILE = "relative rounded-2xl border-2 overflow-hidden bg-black aspect-video transition-[box-shadow,border-color] duration-500";
const GLOW_AI = "border-indigo-400 shadow-[0_0_28px_rgba(129,140,248,0.45)]";
const GLOW_YOU = "border-teal-400 shadow-[0_0_28px_rgba(45,212,191,0.45)]";

// Ava (left) and the candidate (right) side by side, like a video call.
export function VideoFrame({ videoRef, aiSpeaking, voiceOn, listening, idx, total, started, thinking, followUp, question, children }) {
  return (
    <div data-testid="video-frame">
      <div className="flex items-center justify-between gap-2 mb-3 min-h-[26px]">
        {started ? <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold tracking-wider px-2 py-1 rounded-md bg-rose-500/10 text-rose-300" data-testid="live-badge"><span className="tp-rec-pulse" />LIVE · RECORDING</span> : <span />}
        {total > 0 && (
          <span className="whitespace-nowrap text-[11px] font-medium px-2 py-1 rounded-md bg-white/5 text-slate-300" data-testid="question-counter">
            <span className="hidden sm:inline">Main question </span><span className="sm:hidden">Q</span>{Math.min(idx + 1, total)}<span className="hidden sm:inline"> of </span><span className="sm:hidden">/</span>{total}{followUp ? " · follow-up" : ""}
          </span>
        )}
      </div>
      <div className="mb-4 lg:mb-5 max-w-4xl">
        <div className="text-[11px] uppercase tracking-[0.14em] text-indigo-300/80 mb-1.5">{followUp ? "Follow-up question" : "Current question"}</div>
        <div className="font-display text-lg lg:text-2xl font-semibold leading-snug text-white" data-testid="video-question">{question}</div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:gap-4">
        <div className={`${TILE} ${voiceOn ? GLOW_AI : "border-slate-700"}`} data-testid="ava-panel">
          {children}
          {thinking && !aiSpeaking && (
            <div className="absolute inset-0 z-20 grid place-items-center bg-black/40 backdrop-blur-[2px]" data-testid="ai-thinking">
              <div className="flex flex-col items-center gap-2 px-4 py-3 rounded-xl bg-black/55">
                <div className="flex gap-1.5">
                  {[0, 1, 2].map((i) => <span key={i} className="w-2 h-2 rounded-full bg-indigo-300 animate-bounce" style={{ animationDelay: `${i * 150}ms` }} />)}
                </div>
                <div className="text-xs text-indigo-100">Thinking…</div>
              </div>
            </div>
          )}
        </div>
        <div className={`${TILE} ${listening ? GLOW_YOU : "border-slate-700"}`} data-testid="candidate-panel">
          <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" data-testid="video-preview" />
          <span className="absolute bottom-1.5 left-1.5 z-10 inline-flex items-center gap-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-black/60 text-white backdrop-blur" data-testid="candidate-label">
            <span className={`w-1.5 h-1.5 rounded-full ${listening ? "bg-teal-300 animate-pulse" : "bg-slate-400"}`} /> You
          </span>
        </div>
      </div>
      <div className="h-7 mt-1.5 flex justify-center">
        {aiSpeaking && !voiceOn && (
          <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs px-3 py-1 rounded-full bg-white/5 text-slate-300" data-testid="ai-preparing-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-300 animate-pulse" /> Ava is getting ready…
          </span>
        )}
        {aiSpeaking && voiceOn && (
          <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs px-3 py-1 rounded-full bg-indigo-500/10 text-indigo-200" data-testid="ai-speaking-badge">
            <span className="tp-wave" style={{ height: 14 }}><span /><span /><span /><span /><span /></span><span>Ava is speaking<span className="hidden sm:inline"> — please wait until she finishes</span></span>
          </span>
        )}
      </div>
    </div>
  );
}

export function ControlBar({ aiSpeaking, listening, submitting, typeMode, hasSR, currentQuestion, canSubmit, onResumeMic, onSubmit }) {
  const paused = !aiSpeaking && !listening && !submitting && currentQuestion && hasSR;
  const status = aiSpeaking ? "AI is speaking" : submitting ? "AI is preparing the next question…" : listening ? "Listening…"
    : typeMode && currentQuestion ? (hasSR ? "Typing mode — click the mic to answer by voice" : "Typing mode — type your answer") : paused ? "Mic paused — click the mic to resume" : "Idle";
  return (
    <div className="flex flex-col items-center gap-2" data-testid="control-bar">
      <div className="flex items-center gap-3 sm:gap-5">
        <button type="button" onClick={paused ? onResumeMic : undefined} disabled={!paused} title={paused ? "Resume mic" : status}
          data-testid={paused ? "resume-mic" : "mic-status"}
          className={`relative w-12 h-12 sm:w-14 sm:h-14 rounded-full grid place-items-center border transition-colors ${listening ? "bg-teal-500/15 border-teal-400/60 text-teal-300" : paused ? "bg-amber-500/15 border-amber-400/60 text-amber-300 hover:bg-amber-500/25" : "bg-white/5 border-white/10 text-slate-400"}`}>
          {listening && <span className="absolute inset-0 rounded-full border border-teal-400/50 animate-ping" />}
          {listening || aiSpeaking ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
        </button>
        <button data-testid="done-answering" onClick={onSubmit} disabled={!canSubmit}
          className="inline-flex items-center gap-2.5 whitespace-nowrap text-base sm:text-lg font-semibold px-4 sm:px-12 py-3.5 sm:py-4 sm:min-w-[280px] justify-center rounded-2xl text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 shadow-[0_0_24px_rgba(124,58,237,0.35)] disabled:opacity-45 disabled:shadow-none transition-[opacity,box-shadow] duration-200">
          {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Thinking…</> : <>Done answering <ArrowRight className="w-5 h-5" /></>}
        </button>
        <span className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full grid place-items-center bg-emerald-500/10 border border-emerald-400/40 text-emerald-300" title="Camera on" data-testid="camera-status">
          <Video className="w-6 h-6" /><span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-emerald-400" />
        </span>
      </div>
      <div className={`text-sm ${listening ? "text-teal-300" : paused ? "text-amber-300" : aiSpeaking ? "text-indigo-300" : "text-slate-500"}`} data-testid="mic-status-text">{status}</div>
    </div>
  );
}

function TranscriptActions({ show, typeMode, hasText, onEdit, onClear }) {
  if (!show || !hasText) return null;
  const btn = "inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md border border-white/10 text-slate-300 hover:text-white hover:border-violet-400/60 transition-colors";
  return (
    <div className="flex items-center gap-1.5" data-testid="transcript-actions">
      {!typeMode && <button type="button" data-testid="transcript-edit" onClick={onEdit} className={btn}><Pencil className="w-3 h-3" /> Edit</button>}
      <button type="button" data-testid="transcript-clear" onClick={onClear} className={btn}><Eraser className="w-3 h-3" /> Clear</button>
    </div>
  );
}

export function TranscriptPanel({ transcript, typeMode, hasSR, disabled, terms, onType, onToggleType, onEdit, onClear }) {
  const hasText = !!transcript.trim();
  return (
    <aside className="tp-card p-5 flex flex-col min-h-[320px] lg:min-h-0" data-testid="transcript-panel">
      <div className="flex items-center justify-between gap-2 mb-2 min-h-[26px]">
        <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Live captions & transcript</div>
        <TranscriptActions show={!disabled} typeMode={typeMode} hasText={hasText} onEdit={onEdit} onClear={onClear} />
      </div>
      {typeMode ? (
        <textarea data-testid="video-typed-answer" value={transcript} disabled={disabled} onChange={(e) => onType(e.target.value)} autoFocus
          placeholder="Type or correct your answer here, then click Done answering."
          className="flex-1 text-sm text-slate-200 leading-relaxed bg-[color:var(--bg-void)] rounded-xl p-4 border border-violet-500/50 focus:border-violet-400 outline-none min-h-[220px] resize-none" />
      ) : (
        <div className="flex-1 text-[15px] text-slate-200 leading-relaxed bg-[color:var(--bg-void)] rounded-xl p-4 border border-violet-500/50 shadow-[0_0_20px_rgba(139,92,246,0.18)] overflow-y-auto whitespace-pre-wrap min-h-[220px]" data-testid="video-transcript">
          {transcript ? <><div className="text-xs font-semibold text-slate-400 mb-1">You:</div><Highlighted text={transcript} terms={terms} /></>
            : <span className="text-slate-600">Speak naturally — your words appear here as the AI listens.</span>}
        </div>
      )}
      {(typeMode ? hasSR : hasText) && !disabled && (
        <div className="mt-2 text-[11px] text-slate-500" data-testid="transcript-hint">
          {typeMode ? "Fix any words the captions got wrong. Continuing by voice adds to this text." : "Captions got a word wrong? Click Edit to fix it before you click Done answering."}
        </div>
      )}
      <button type="button" data-testid="toggle-type-mode" onClick={onToggleType} className="mt-1 self-start text-xs text-indigo-300 hover:text-indigo-200">
        {typeMode ? (hasSR ? "Continue answering by voice" : "Voice answers need Chrome or Edge") : "Having trouble with the mic? Type your answer instead"}
      </button>
    </aside>
  );
}
